import { type CfPoolOrders } from '@chainflip/rpc/types';
import { baseChainflipAssets, type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { useQueries } from '@tanstack/react-query';
import { getOpenOrdersByAccountQuery } from '@/shared/graphql/lp/pool_orders';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import { getAccountPoolOrderQueryKey } from '@/shared/queryKeys';
import { type RpcOrder } from '@/shared/utils';
import makeRpcRequest, { getChainAndAsset } from '@/shared/utils/rpc';

export const useOrders = (baseAssets: BaseChainflipAsset[] = baseChainflipAssets) => {
  const { selectedAccount: polkadotAccount } = usePolkadot();

  const { data: processorOpenOrders } = useGqlQuery(getOpenOrdersByAccountQuery, {
    variables: {
      idSs58: polkadotAccount!.idSs58,
    },
    context: { clientName: 'lpProcessor' },
    refetchInterval: 10_000,
    enabled: Boolean(polkadotAccount?.idSs58),
  });

  const queries = useQueries({
    queries:
      baseAssets.map((baseAsset) => ({
        enabled: Boolean(polkadotAccount?.idSs58),
        refetchInterval: 60_000,
        queryKey: getAccountPoolOrderQueryKey(baseAsset, 'Usdc', polkadotAccount?.idSs58 ?? ''),
        queryFn: () =>
          polkadotAccount &&
          makeRpcRequest(
            'cf_pool_orders',
            getChainAndAsset(baseAsset),
            getChainAndAsset('Usdc'),
            polkadotAccount?.idSs58,
            true,
          ),
        select: (result: CfPoolOrders | null): RpcOrder[] => {
          if (!result) return [];

          return [
            ...(result?.range_orders?.map((order) => ({
              ...order,
              asset: baseAsset,
            })) || []),
            ...(result?.limit_orders?.bids.map((order) => ({
              ...order,
              asset: baseAsset,
            })) || []),
            ...(result?.limit_orders.asks.map((order) => ({
              ...order,
              asset: baseAsset,
            })) || []),
          ];
        },
      })) || [],
  });

  const rpcOrders = queries
    .filter((item) => item.data != null)
    .flatMap((item) => item.data as RpcOrder[])
    .sort((a, b) => b.id.localeCompare(a.id));

  const openRpcOrders = rpcOrders.filter(
    (order) => order.type === 'range' || order.sell_amount > 0,
  ); // hide fully filled limit orders

  return {
    rpcOrders,
    openRpcOrders,
    processorOpenOrders,
  };
};
